import styled, { css } from 'styled-components';

const keyboardFocus = css`
  outline: 3px solid ${({ theme }) => theme.colors.softBlack};
  outline-offset: 2px;
`;

export const Section = styled.section`
  margin-top: 35px;
  padding: 24px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.white};
`;

export const SectionHeader = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;

  h2 {
    margin: 0;
    color: ${({ theme }) => theme.colors.softBlack};
    font-size: 22px;
  }

  p {
    margin: 8px 0 0;
    color: ${({ theme }) => theme.colors.grey};
    font-size: 13px;
  }

  @media only screen and (max-width: ${({ theme }) => theme.breakpoints.xl}px) {
    flex-direction: column;
  }
`;

export const Toolbar = styled.div`
  margin-top: 24px;
`;

export const Tabs = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 8px;
`;

export const Tab = styled.button<{ active: boolean }>`
  flex: 0 0 auto;
  border: 1px solid
    ${({ theme, active }) => (active ? theme.colors.main : theme.colors.lightGrey)};
  border-radius: 5px;
  padding: 10px 13px;
  background: ${({ theme, active }) =>
    active ? theme.colors.main : theme.colors.white};
  color: ${({ theme, active }) =>
    active ? theme.colors.white : theme.colors.softBlack};
  cursor: pointer;

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.softBlack};
    outline-offset: 2px;
  }
`;

export const Filters = styled.div`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 18px;
`;

export const FilterGroup = styled.fieldset`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin: 0;
  padding: 0;
  border: 0;

  legend {
    width: 100%;
    margin-bottom: 6px;
    color: ${({ theme }) => theme.colors.softBlack};
    font-size: 12px;
    font-weight: 700;
  }
`;

export const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 160px;
  color: ${({ theme }) => theme.colors.softBlack};
  font-size: 12px;
  font-weight: 600;

  input,
  select {
    min-height: 38px;
    border: 1px solid ${({ theme }) => theme.colors.lightGrey};
    border-radius: 4px;
    padding: 0 9px;
    background: ${({ theme }) => theme.colors.white};
    color: ${({ theme }) => theme.colors.softBlack};
    font: inherit;

    &:focus-visible {
      ${keyboardFocus}
    }
  }
`;

export const WhatsAppButton = styled.button`
  min-height: 38px;
  border: 0;
  border-radius: 4px;
  padding: 0 16px;
  background: ${({ theme }) => theme.colors.main};
  color: ${({ theme }) => theme.colors.white};
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &:focus-visible {
    ${keyboardFocus}
  }
`;

export const ApplyButton = styled.button`
  min-height: 38px;
  border: 0;
  border-radius: 4px;
  padding: 0 16px;
  background: ${({ theme }) => theme.colors.softBlack};
  color: ${({ theme }) => theme.colors.white};
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &:focus-visible {
    ${keyboardFocus}
  }
`;

export const ExportButton = styled.button`
  min-height: 38px;
  border: 1px solid ${({ theme }) => theme.colors.main};
  border-radius: 4px;
  padding: 0 16px;
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.main};
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &:focus-visible {
    ${keyboardFocus}
  }
`;

export const FilterActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 10px;
`;

export const TableContainer = styled.div`
  margin-top: 22px;
  overflow-x: auto;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 5px;
`;

export const Table = styled.table`
  width: 100%;
  min-width: 920px;
  border-collapse: collapse;
  color: ${({ theme }) => theme.colors.softBlack};
  font-size: 13px;

  th,
  td {
    padding: 11px 12px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
    text-align: left;
    white-space: nowrap;
  }

  th {
    background: ${({ theme }) => theme.colors.lightGrey};
    font-size: 12px;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }
`;

export const SortButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 32px;
  width: calc(100% + 24px);
  margin: -11px -12px;
  border: 0;
  padding: 11px 12px;
  background: transparent;
  color: inherit;
  font: inherit;
  font-weight: inherit;
  text-align: left;
  cursor: pointer;

  &:focus-visible {
    ${keyboardFocus}
  }
`;

export const SortIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  width: 18px;
  height: 18px;
  line-height: 1;

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const CheckboxHeaderCell = styled.th`
  width: 42px;
  text-align: center;

  input {
    width: 16px;
    height: 16px;
    margin: 0;
    cursor: pointer;
  }
`;

export const CheckboxCell = styled.td`
  width: 42px;
  text-align: center;

  input {
    width: 16px;
    height: 16px;
    margin: 0;
    cursor: pointer;
  }
`;

export const Badge = styled.span<{ tone: 'curve' | 'risk' }>`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 4px 8px;
  background: ${({ theme, tone }) =>
    tone === 'risk' ? theme.colors.orange : theme.colors.main};
  color: ${({ theme }) => theme.colors.white};
  font-size: 11px;
  font-weight: 700;
`;

export const Status = styled.div<{ error?: boolean }>`
  margin-top: 20px;
  padding: 16px;
  border-radius: 5px;
  background: ${({ theme, error }) =>
    error ? theme.colors.lightGrey : theme.colors.white};
  color: ${({ theme, error }) =>
    error ? theme.colors.red : theme.colors.grey};
`;

export const RetryButton = styled.button`
  margin-top: 10px;
  border: 1px solid ${({ theme }) => theme.colors.main};
  border-radius: 4px;
  padding: 8px 12px;
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.main};
  cursor: pointer;

  &:focus-visible {
    ${keyboardFocus}
  }
`;

export const Pagination = styled.footer`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
  color: ${({ theme }) => theme.colors.grey};
  font-size: 13px;

  div {
    display: flex;
    gap: 8px;
  }

  button {
    border: 1px solid ${({ theme }) => theme.colors.lightGrey};
    border-radius: 4px;
    padding: 8px 11px;
    background: ${({ theme }) => theme.colors.white};
    color: ${({ theme }) => theme.colors.softBlack};
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    &:focus-visible {
      ${keyboardFocus}
    }
  }
`;
